import React, { useEffect, useMemo } from 'react';
import { Drawer, Alert, Button, FormRow } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FaBan, FaSave } from 'react-icons/fa';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js';
import PropTypes from 'prop-types';
import { CRM_SALUTATION } from 'app_config';
import { salesEmployeesByNameDebounce } from 'helpers/employeeOptionHelper';
import { createContact } from 'redux/crm/contactSlice';
import { getCustomer } from 'redux/crm/customerSlice';
import { INPUT_CLS, SELECT_STYLES } from 'helpers/formStyles';

const ContactFormModal = ({ showModal, setshowModal, customerId }) => {
  const { data: profileUser } = useSelector(state => state.profile_user);
  const { recordData: customer } = useSelector(state => state.crm_customer);
  const { control, register, handleSubmit, formState: { errors }, setValue } = useForm();
  const dispatch = useDispatch();

  const salutationOptions = useMemo(() => CRM_SALUTATION.map(opt => ({ value: opt, label: opt })), []);
  const defaultSalutationOption = useMemo(() => salutationOptions[1], [salutationOptions]);
  const defaultMainContactOption = useMemo(() => ({ value: profileUser.name, label: profileUser.name }), [profileUser.name]);

  useEffect(() => {
    if (customerId) dispatch(getCustomer(customerId));
  }, [customerId, dispatch]);

  useEffect(() => {
    setValue('salutation', salutationOptions[1]?.value);
    setValue('main_contact', profileUser.name);
    // mount-only: seed form defaults once; re-running would overwrite what the user typed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = () => setshowModal(false);

  const validatePhoneNumber = (number) => {
    if (!number || number.trim() === '') return undefined;
    const countryCode = customer?.country ?? 'VN';
    if (!isValidPhoneNumber(number.toString(), countryCode)) {
      return `Invalid phone number with country code ${countryCode}.`;
    }
    return true;
  };

  const validate = (data) => {
    if ((!data.email || data.email === '') && (!data.mobile || data.mobile === '')) {
      toast.error('Email or mobile must be required.');
      return false;
    }
    return true;
  };

  const onSubmit = async (data) => {
    if (!validate(data)) return;
    try {
      const response = await dispatch(createContact({ customerId, data }));
      if (response.payload?.data?.id) closeModal();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const loadContactOptions = (inputValue, callback) => {
    salesEmployeesByNameDebounce(inputValue, callback);
  };

  const footer = (
    <>
      <Button type="submit" size="sm" onClick={handleSubmit(onSubmit)}>
        <FaSave size={12} /> Save
      </Button>
      <Button variant="danger" size="sm" onClick={closeModal}>
        <FaBan size={12} /> Cancel
      </Button>
    </>
  );

  return (
    <Drawer
      show={showModal}
      onHide={closeModal}
      title="New Contact"
      width="50%"
      backdropClick={false}
      footer={footer}
    >
      <Alert type="warning" className="tw-mb-4">
        Create a new contact in <strong>{customer?.name}</strong> customer.
      </Alert>

      <FormRow label="Salutation" required error={errors.salutation?.message}>
        <Controller
          control={control}
          name="salutation"
          rules={{ required: 'Salutation is required' }}
          render={() => (
            <Select
              closeMenuOnSelect
              options={salutationOptions}
              defaultValue={defaultSalutationOption}
              placeholder="Select..."
              menuPosition="fixed"
              styles={SELECT_STYLES}
              onChange={opt => setValue('salutation', opt.value, { shouldValidate: true })}
            />
          )}
        />
      </FormRow>

      <FormRow label="Contact name" required error={errors.first_name?.message}>
        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
          <div>
            <input
              type="text"
              placeholder="First name"
              className={INPUT_CLS}
              {...register('first_name', { required: 'First name must be required.' })}
            />
          </div>
          <div>
            <input
              type="text"
              placeholder="Last name"
              className={INPUT_CLS}
              {...register('last_name')}
            />
          </div>
        </div>
      </FormRow>

      <FormRow label="Title" required error={errors.position?.message}>
        <input
          type="text"
          placeholder="Title"
          className={INPUT_CLS}
          {...register('position', { required: 'Title must be required.' })}
        />
      </FormRow>

      <FormRow label="Email" error={errors.email?.message}>
        <input
          type="email"
          placeholder="example@pebsteel.com.vn"
          className={INPUT_CLS}
          {...register('email', {
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
              message: 'Invalid email address',
            },
          })}
        />
      </FormRow>

      <FormRow label="Phone number" error={errors.mobile?.message}>
        <Controller
          control={control}
          name="mobile"
          rules={{ validate: validatePhoneNumber }}
          render={({ field: { ref, ...field } }) => (
            <PhoneInput
              {...field}
              inputProps={{ ref }}
              country={customer?.country?.toLowerCase() ?? 'vn'}
              autoFormat
              countryCodeEditable={false}
              enableSearch
              disableSearchIcon
              inputStyle={{ width: '100%' }}
              searchStyle={{ width: '95%' }}
            />
          )}
        />
      </FormRow>

      <FormRow label="Main contact" required error={errors.main_contact?.message}>
        <Controller
          control={control}
          name="main_contact"
          rules={{ required: 'Main contact is required' }}
          render={() => (
            <AsyncSelect
              cacheOptions
              defaultOptions
              defaultValue={defaultMainContactOption}
              loadOptions={loadContactOptions}
              closeMenuOnSelect
              placeholder="Select or input 3 characters of name..."
              menuPosition="fixed"
              styles={SELECT_STYLES}
              onChange={opt => setValue('main_contact', opt.value)}
            />
          )}
        />
      </FormRow>

      <FormRow label="Secondary contact">
        <Controller
          control={control}
          name="secondary_contact"
          render={() => (
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={loadContactOptions}
              closeMenuOnSelect={false}
              placeholder="Select..."
              menuPosition="fixed"
              styles={SELECT_STYLES}
              isMulti
              onChange={opts => setValue('secondary_contact', opts.map(o => o.value), { shouldValidate: true })}
            />
          )}
        />
      </FormRow>
    </Drawer>
  );
};

ContactFormModal.propTypes = {
  showModal:    PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  customerId:   PropTypes.number.isRequired,
};

export default ContactFormModal;
